import React, { Fragment, useState } from 'react'

import Icon from 'components_new/atoms/Icon'
import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableContainer from 'components_new/atoms/Table/TableContainer'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import SimpleTable from 'components_new/molecules/SimpleTable'

import DataRerunJobName from 'components_new/organisms/internal/DataReruns/JobName'

import { DataRerunCustomer } from 'redux/selectors/internal/DataReruns'
import type { TDataRerunJobDto } from '@homepal/api-contract'
import {
  formatCostUsd,
  formatDayClock,
  formatDurationMin,
  STATUS_COLOR,
  STATUS_LABEL
} from 'components_new/organisms/internal/DataRuns/utils'
import {
  customerCostUsd,
  customerStatusDisplay,
  formatSkippedReason
} from 'components_new/organisms/internal/DataReruns/utils'

const COLUMNS = [
  { title: '', width: 32 },
  { title: 'Kund' },
  { title: 'Status', width: 140 },
  { title: 'Startad', width: 100 },
  { title: 'Klar', width: 100 },
  { title: 'Kostnad', align: 'right' as const, width: 90 }
]

const JOB_COLUMNS = [
  { title: 'Jobb' },
  { title: 'Status', width: 120 },
  { title: 'Startad', width: 100 },
  { title: 'Längd', align: 'right' as const, width: 80 },
  { title: 'Kostnad', align: 'right' as const, width: 90 }
]

const UNSTARTED_JOB_LABEL: Record<'waiting' | 'not_started', string> = {
  waiting: 'Väntar',
  not_started: 'Startades inte'
}

interface DataRerunCustomerTableProps {
  customers: DataRerunCustomer[]
}

/** Status, Startad, Längd and Kostnad cells of one job. */
function jobStateCells(job: TDataRerunJobDto): React.ReactNode[] {
  if (job.state === 'waiting' || job.state === 'not_started') {
    return [
      <Tag key="state" label={UNSTARTED_JOB_LABEL[job.state]} />,
      '',
      '',
      ''
    ]
  }

  return [
    <Tag
      color={STATUS_COLOR[job.state]}
      key="state"
      label={STATUS_LABEL[job.state]}
    />,
    formatDayClock(job.started_at),
    formatDurationMin(job.duration_min),
    formatCostUsd(job.cost_usd)
  ]
}

const CustomerDetail = (props: { customer: DataRerunCustomer }) => {
  const { customer } = props

  if (customer.status === 'skipped') {
    return (
      <Text sx={{ color: 'warning.text' }} variant="body2">
        {formatSkippedReason(customer.skipped_reason)}
      </Text>
    )
  }

  if (customer.jobs.length === 0) {
    return (
      <Text sx={{ color: 'text.secondary' }} variant="body2">
        Inga jobb startade ännu.
      </Text>
    )
  }

  return (
    <SimpleTable
      columns={JOB_COLUMNS}
      disableSorting
      rows={customer.jobs.map((job) => ({
        id: job.job_name,
        cells: [
          <DataRerunJobName
            jobName={job.job_name}
            key="name"
            label={job.label}
          />,
          ...jobStateCells(job)
        ]
      }))}
    />
  )
}

/**
 * The customers of one rerun, by priority. A row expands to the Glue runs
 * started for it.
 */
const DataRerunCustomerTable = (props: DataRerunCustomerTableProps) => {
  const { customers } = props

  const [expandedId, setExpandedId] = useState<string | null>(null)

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map((column, i) => (
              <TableCell
                align={column.align}
                key={i}
                sx={{ whiteSpace: 'nowrap', width: column.width }}
              >
                {column.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {customers.map((customer) => {
            const expanded = expandedId === customer.id
            const status = customerStatusDisplay(customer)

            return (
              <Fragment key={customer.id}>
                <TableRow
                  hover
                  onClick={() => setExpandedId(expanded ? null : customer.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Icon
                      fontSize="small"
                      name={
                        expanded ? 'KeyboardArrowDown' : 'KeyboardArrowRight'
                      }
                      sx={{ color: 'text.disabled' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Text sx={{ fontWeight: 600 }} variant="body2">
                      {customer.customer_name}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Tag color={status.color} label={status.label} />
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatDayClock(customer.started_at)}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatDayClock(customer.finished_at)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatCostUsd(customerCostUsd(customer))}
                  </TableCell>
                </TableRow>
                {expanded ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length}
                      sx={{ bgcolor: 'background.default', p: '10px 14px' }}
                    >
                      <CustomerDetail customer={customer} />
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

DataRerunCustomerTable.displayName = 'DataRerunCustomerTable'
export default DataRerunCustomerTable
